import React, { type ReactNode, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { radii, spacing, typography } from '../../core/theme/tokens';
import {
  buttonTextColor,
  buttonVariantTint,
  GlassSurface,
  type GlassButtonVariant,
} from './glass';

export type GlassButtonProps = Omit<PressableProps, 'children' | 'style'> & {
  children: ReactNode;
  variant?: GlassButtonVariant;
  size?: 'md' | 'sm' | 'icon';
  fullWidth?: boolean;
  selected?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

const MIN_TOUCH = 44;

export function GlassButton({
  children,
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  selected = false,
  style,
  textStyle,
  disabled,
  ...pressableProps
}: GlassButtonProps) {
  const [hovered, setHovered] = useState(false);
  const isGhost = variant === 'ghost';
  const isIcon = size === 'icon';
  const tint = selected ? 'accent' : buttonVariantTint[variant];
  const showSurface = !isGhost;

  return (
    <Pressable
      accessibilityRole="button"
      disabled={disabled}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      {...pressableProps}
      style={({ pressed }) => [
        styles.pressable,
        fullWidth && styles.fullWidth,
        isIcon && styles.iconPressable,
        disabled && styles.disabled,
        (pressed || hovered) && !disabled && styles.interactive,
        style,
      ]}
    >
      {showSurface ? (
        <GlassSurface
          tint={tint === 'none' ? 'neutral' : tint}
          rounded={isIcon ? 'md' : 'pill'}
          showHairline={variant === 'primary' || selected || hovered}
          style={[
            styles.surface,
            isIcon && styles.iconSurface,
            size === 'sm' && styles.surfaceSm,
            size === 'md' && styles.surfaceMd,
            hovered && styles.surfaceHover,
          ]}
        >
          {renderLabel(children, variant, size, textStyle, selected)}
        </GlassSurface>
      ) : (
        <View
          style={[
            styles.ghostInner,
            size === 'sm' && styles.surfaceSm,
            size === 'md' && styles.surfaceMd,
            hovered && styles.ghostHover,
          ]}
        >
          {renderLabel(children, variant, size, textStyle, selected)}
        </View>
      )}
    </Pressable>
  );
}

function renderLabel(
  children: ReactNode,
  variant: GlassButtonVariant,
  size: 'md' | 'sm' | 'icon',
  textStyle: StyleProp<TextStyle> | undefined,
  selected: boolean,
) {
  if (typeof children !== 'string' && typeof children !== 'number') {
    return children;
  }

  const label = String(children);
  return (
    <Text
      style={[
        size === 'icon' ? styles.iconText : size === 'sm' ? styles.textSm : styles.textMd,
        { color: selected ? buttonTextColor.primary : buttonTextColor[variant] },
        textStyle,
      ]}
    >
      {label}
    </Text>
  );
}

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'flex-start',
    cursor: 'pointer',
  } as object,
  fullWidth: {
    alignSelf: 'stretch',
    width: '100%',
  },
  iconPressable: {
    alignSelf: 'center',
  },
  disabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  } as object,
  interactive: {
    opacity: 0.95,
  },
  surface: {
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  } as object,
  surfaceHover: {
    transform: [{ translateY: -1 }],
    boxShadow:
      '0 12px 36px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
  } as object,
  iconSurface: {
    width: MIN_TOUCH,
    height: MIN_TOUCH,
    minWidth: MIN_TOUCH,
    minHeight: MIN_TOUCH,
  },
  surfaceSm: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  surfaceMd: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    minHeight: MIN_TOUCH,
  },
  ghostInner: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.sm,
  },
  ghostHover: {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
  },
  textMd: {
    ...typography.headline,
    fontSize: 17,
    textAlign: 'center',
  },
  textSm: {
    ...typography.caption,
    fontWeight: '600',
    textAlign: 'center',
  },
  iconText: {
    ...typography.headline,
    fontSize: 20,
    lineHeight: 22,
    textAlign: 'center',
  },
});
